import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LangProvider } from "@/contexts/LangContext";
import ProtectedRoute, { RequirePerm } from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import AppShell from "@/components/AppShell";
import { ConfigProvider } from "@/contexts/ConfigContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Pipeline from "@/pages/Pipeline";
import Clients from "@/pages/Clients";
import Activities from "@/pages/Activities";
import Reports from "@/pages/Reports";
import Team from "@/pages/Team";
import Approvals from "@/pages/Approvals";
import AuditLog from "@/pages/AuditLog";
import Admin from "@/pages/Admin";
import Profile from "@/pages/Profile";
import NoAccess from "@/pages/NoAccess";

const NoAccessRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <NoAccess />;
};

function AppRouter() {
  const location = useLocation();
  // Synchronous check for OAuth callback fragment (must be before ProtectedRoute)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/no-access" element={<NoAccessRoute />} />
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/pipeline" element={<Pipeline />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/activities" element={<Activities />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/approvals" element={<RequirePerm perm="deals:approve"><Approvals /></RequirePerm>} />
        <Route path="/team" element={<RequirePerm perm="users:manage"><Team /></RequirePerm>} />
        <Route path="/admin" element={<RequirePerm perm="users:manage"><Admin /></RequirePerm>} />
        <Route path="/audit-log" element={<RequirePerm perm="audit:read"><AuditLog /></RequirePerm>} />
      </Route>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LangProvider>
          <AuthProvider>
            <ConfigProvider>
              <AppRouter />
              <Toaster position="top-right" richColors />
            </ConfigProvider>
          </AuthProvider>
        </LangProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
